/*
 * Edge fades on the wide response tables (org event dashboard + read-only
 * viewer). A table that fits the screen except for its last few columns looked
 * complete — nothing said there was more to the side. The frame around the
 * scroll container fades whichever edge has columns out of view; the left fade
 * starts past the frozen columns (#, checkbox, Name) so it never covers them.
 *
 * Markup: <div class="table-fade-frame"><div class="... is-spreadsheet-wrap">…
 * Both dashboards re-render the table wholesale, so frames are found by a
 * MutationObserver rather than wired by each renderer.
 */
(function () {
  'use strict';
  const FRAME_SEL = '.table-fade-frame';
  const WRAP_SEL = '.is-spreadsheet-wrap';
  const SLACK = 2; // px of sub-pixel rounding that still counts as "at the edge"

  function update(frame) {
    const wrap = frame.querySelector(WRAP_SEL);
    if (!wrap) return;
    const max = wrap.scrollWidth - wrap.clientWidth;
    // RTL scrollLeft runs from 0 (start, right edge) down to -max.
    const rtl = getComputedStyle(wrap).direction === 'rtl';
    const hiddenLeft = rtl ? max + wrap.scrollLeft : wrap.scrollLeft;
    frame.classList.toggle('has-more-left', max > SLACK && hiddenLeft > SLACK);
    frame.classList.toggle('has-more-right', max > SLACK && hiddenLeft < max - SLACK);

    // Frozen columns stick with a physical `left`, so measure their right edge.
    let frozenEdge = 0;
    const wrapRect = wrap.getBoundingClientRect();
    wrap.querySelectorAll('thead th').forEach(th => {
      const cs = getComputedStyle(th);
      if (cs.position !== 'sticky' || cs.left === 'auto') return;
      frozenEdge = Math.max(frozenEdge, th.getBoundingClientRect().right - wrapRect.left);
    });
    frame.style.setProperty('--fade-frozen', `${Math.max(0, Math.round(frozenEdge))}px`);
    // Keep the fade off the horizontal scrollbar.
    const cs = getComputedStyle(wrap);
    const scrollbar = wrap.offsetHeight - wrap.clientHeight
      - parseFloat(cs.borderTopWidth) - parseFloat(cs.borderBottomWidth);
    frame.style.setProperty('--fade-scrollbar', `${Math.max(0, scrollbar)}px`);
  }

  const pending = new Set();
  let raf = 0;
  function schedule(frame) {
    pending.add(frame);
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      pending.forEach(f => { if (f.isConnected) update(f); });
      pending.clear();
    });
  }

  // Column resizing and cell edits change the table's width without a scroll.
  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(entries => entries.forEach(e => {
        const frame = e.target.closest(FRAME_SEL);
        if (frame) schedule(frame);
      }))
    : null;
  const observed = new WeakSet();

  function scan() {
    document.querySelectorAll(FRAME_SEL).forEach(frame => {
      schedule(frame);
      const table = frame.querySelector('table');
      if (resizeObserver && table && !observed.has(table)) {
        observed.add(table);
        resizeObserver.observe(frame);
        resizeObserver.observe(table);
      }
    });
  }

  // Scroll doesn't bubble; catch it on the way down.
  document.addEventListener('scroll', (e) => {
    const t = e.target;
    if (t instanceof Element && t.matches(WRAP_SEL)) {
      const frame = t.closest(FRAME_SEL);
      if (frame) schedule(frame);
    }
  }, true);
  window.addEventListener('resize', scan);

  let scanQueued = false;
  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(() => { scanQueued = false; scan(); });
  }
  function start() {
    new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
    scan();
  }
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
